/**
 * Utility functions for working with chatbot settings
 * Ensures consistent parsing and serialization of settings JSON
 */

/**
 * Parses settings from database format (string or object) to object
 * @param settings - Settings from database (can be string or object)
 * @returns Parsed settings object, or empty object if parsing fails
 */
export function parseSettings(settings: any): any {
  if (!settings) {
    return {};
  }

  // If already an object, return as-is
  if (typeof settings === 'object' && settings !== null) {
    return settings;
  }

  // If it's a string, try to parse it
  if (typeof settings === 'string') {
    try {
      return JSON.parse(settings);
    } catch (e) {
      console.error('❌ Error parsing settings JSON:', e);
      console.error('❌ Invalid JSON string:', settings.substring(0, 200));
      return {};
    }
  }

  // Fallback to empty object
  return {};
}

/**
 * Serializes settings object to JSON string for database storage
 * @param settings - Settings object to serialize
 * @returns JSON string representation
 */
export function serializeSettings(settings: any): string {
  if (!settings || typeof settings !== 'object') {
    return '{}';
  }

  try {
    return JSON.stringify(settings);
  } catch (e) {
    console.error('❌ Error serializing settings to JSON:', e);
    return '{}';
  }
}

/**
 * Merges new settings with existing settings, handling both string and object formats
 * @param existingSettings - Existing settings from database (can be string or object)
 * @param newSettings - New settings to merge
 * @returns Merged settings object
 */
export function mergeSettings(existingSettings: any, newSettings: any): any {
  const parsedExisting = parseSettings(existingSettings);
  const parsedNew = parseSettings(newSettings);

  return {
    ...parsedExisting,
    ...parsedNew
  };
}




